"use client";

import type { ConversationData } from "@/features/chat/Types/conversations.ts";
import type {
  DayEntry as ApiDayEntry,
  Message,
  MessageData,
  MessageGroup,
} from "@/features/chat/Types/messages.ts";
import type React from "react";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useDebouncedCallback } from "use-debounce";
import { useEcho } from "@laravel/echo-react";
import { useParams } from "react-router-dom";
import { format, toZonedTime } from "date-fns-tz";

import { toast } from "@/utils/customToast";
import useUserStore from "@/features/auth/store/useUserStore.ts";
import { useChatStore } from "@/features/chat/store/useChatStore.tsx";
import useWorkspaceInformationStore from "@/features/workspaces/store/useWorkspaceInformationStore.tsx";
import {
  deleteMessageFromApi,
  deleteMessageFromLessonChat,
  getConversationData,
  getConversationMessages,
  getLessonConversationMessages,
  markConversationAsRead,
  sendMessageToApi,
  sendMessageToLessonChat,
  updateMessageInApi,
  updateMessageInLessonChat,
} from "@/features/chat/api/chatApi.tsx";

// Add TanStack Query imports at the top

// Maximum file upload constraints as per documentation
export const MAX_FILE_COUNT = 5;
export const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB in bytes
export const MAX_TOTAL_SIZE = 75 * 1024 * 1024; // 75MB in bytes
export const MAX_CONVERSATION_SIZE = 5 * 1024 * 1024 * 1024; // 5GB in bytes
export const ALLOWED_FILE_TYPES = [
  // Images
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  // Documents
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv",
  // Videos
  "video/mp4",
  "video/webm",
  "video/ogg",
  "video/mpeg",
  "video/quicktime",
  // Audio
  "audio/mpeg",
  "audio/ogg",
  "audio/wav",
  "audio/webm",
  "audio/mp4",
];

export type PendingMessage = {
  id: string;
  localId: string; // Add localId to prevent re-animations
  content: string;
  files: File[];
  timestamp: Date | string;
  status: "sending" | "error" | "transitioning" | "delivered";
  realMessageId?: string;
  deliveredData?: Message;
  created_at: string;
};

export type DayEntry = ApiDayEntry;

// Add rate limiting types
type RateLimitLevel = "normal" | "warning" | "restricted" | "severe";

interface RateLimitState {
  level: RateLimitLevel;
  messageTimestamps: number[];
  lastViolation: number | null;
  cooldownEnd: number | null;
  remainingCooldown: number;
}

// Rate limiting configuration
const RATE_LIMITS = {
  normal: { maxMessages: 10, window: 5000, cooldown: 0 }, // 10 messages per 5 seconds
  warning: { maxMessages: 5, window: 5000, cooldown: 10000 }, // 5 messages per 5 seconds, 10s cooldown
  restricted: { maxMessages: 1, window: 5000, cooldown: 30000 }, // 1 message per 5 seconds, 30s cooldown
  severe: { maxMessages: 1, window: 10000, cooldown: 60000 }, // 1 message per 10 seconds, 60s cooldown
} as const;

// Message grouping configuration
const MESSAGE_GROUPING = {
  maxTimeDifference: 5 * 60 * 1000, // 5 minutes in milliseconds
} as const;

// Utility function to check if messages should be grouped together
const shouldGroupMessages = (
  message1: Message,
  message2: Message,
  user1Id: string,
  user2Id: string,
): boolean => {
  // Messages must be from the same user
  if (user1Id !== user2Id) {
    return false;
  }

  // Check if the time difference is within the grouping threshold
  const time1 = new Date(message1.created_at).getTime();
  const time2 = new Date(message2.created_at).getTime();
  const timeDifference = Math.abs(time1 - time2);

  return timeDifference <= MESSAGE_GROUPING.maxTimeDifference;
};

// Extend the Message type to include pending properties
type ExtendedMessage = Message & {
  user?: any;
  pending?: boolean;
  localId?: string;
  files?: File[];
  error?: boolean;
};

// Update the useChat hook to include infinite scrolling functionality
export function useChat(
  conversationId: string,
  type?: "normal" | "lesson",
  onNewMessageFromOthers?: (message: any) => void,
) {
  // Existing state
  const [isOpen, setIsOpen] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [activeConversation, setActiveConversation] = useState<ConversationData | null>(null);
  const [view, setView] = useState<"list" | "conversation">("conversation");
  const [blockedUsers, setBlockedUsers] = useState<string[]>([]);
  const { user: currentUser } = useUserStore();
  const { conversationList } = useChatStore();
  const { data: workspaceData } = useWorkspaceInformationStore();
  const [selectedFiles, setSelectedFiles] = useState<
    { file: File; isValid: boolean; validationError: string }[]
  >([]);
  const [isSending, setIsSending] = useState(false);
  const { t } = useTranslation(["common", "chat"]);
  const [animatedMessageIds, setAnimatedMessageIds] = useState<Set<string>>(new Set());
  const { workspaceId, lessonId } = useParams();

  // Computed property for backward compatibility
  const selectedFilesCount = selectedFiles.length;

  // Add state for drag-and-drop overlay
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  // Add rate limiting state
  const [rateLimitState, setRateLimitState] = useState<RateLimitState>({
    level: "normal",
    messageTimestamps: [],
    lastViolation: null,
    cooldownEnd: null,
    remainingCooldown: 0,
  });

  // Add this new state near the top of the hook, with the other state declarations
  const [typingUsers, setTypingUsers] = useState<{
    [key: string]: { timestamp: number; user: any };
  }>({});

  // Add this new state for tracking message read status
  const [hasFocus, setHasFocus] = useState(document.hasFocus());

  // Add new state for local messages
  const [localMessages, setLocalMessages] = useState<MessageData | null>(null);

  // Update rate limit cooldown timer
  useEffect(() => {
    if (rateLimitState.cooldownEnd) {
      const interval = setInterval(() => {
        const now = Date.now();
        const remaining = Math.max(0, rateLimitState.cooldownEnd! - now);

        setRateLimitState((prev) => ({
          ...prev,
          remainingCooldown: remaining,
        }));

        if (remaining <= 0) {
          // Cooldown finished, potentially reduce restriction level
          setRateLimitState((prev) => {
            let newLevel = prev.level;

            // Gradually reduce restriction level after cooldown
            if (prev.level === "severe") newLevel = "restricted";
            else if (prev.level === "restricted") newLevel = "warning";
            else if (prev.level === "warning") newLevel = "normal";

            return {
              ...prev,
              level: newLevel,
              cooldownEnd: null,
              remainingCooldown: 0,
              // Clean old timestamps when returning to normal
              messageTimestamps: newLevel === "normal" ? [] : prev.messageTimestamps,
            };
          });
          clearInterval(interval);
        }
      }, 100);

      return () => clearInterval(interval);
    }
  }, [rateLimitState.cooldownEnd]);

  // Check if user can send message based on rate limits and workspace blocking
  const canSendMessage = useCallback((): {
    allowed: boolean;
    reason?: string;
    timeRemaining?: number;
    isWorkspaceBlocked?: boolean;
  } => {
    // Check workspace blocking first (takes precedence over rate limiting)
    if (workspaceData && activeConversation) {
      const isBlocked = workspaceData.is_workspace_blocked_for_user;
      const hasLeft = workspaceData.has_user_left_workspace;
      const conversationType = activeConversation.type;

      // For individual conversations: only block if user is blocked
      if (conversationType === "individual" && isBlocked) {
        return {
          allowed: false,
          reason: t("chat:workspaceBlocking.blocked" as any),
          isWorkspaceBlocked: true,
        };
      }

      // For group conversations: block if user is blocked OR has left
      if (conversationType === "group" && (isBlocked || hasLeft)) {
        if (isBlocked) {
          return {
            allowed: false,
            reason: t("chat:workspaceBlocking.blocked" as any),
            isWorkspaceBlocked: true,
          };
        } else if (hasLeft) {
          return {
            allowed: false,
            reason: t("chat:workspaceBlocking.leftWorkspace" as any),
            isWorkspaceBlocked: true,
          };
        }
      }
    }

    const now = Date.now();
    const currentLimit = RATE_LIMITS[rateLimitState.level];

    // If in cooldown, deny sending
    if (rateLimitState.cooldownEnd && now < rateLimitState.cooldownEnd) {
      const timeRemaining = Math.ceil((rateLimitState.cooldownEnd - now) / 1000);

      return {
        allowed: false,
        reason: t("chat:rateLimiting.cooldown"),
        timeRemaining,
      };
    }

    // Clean old timestamps outside the current window
    const validTimestamps = rateLimitState.messageTimestamps.filter(
      (timestamp) => now - timestamp < currentLimit.window,
    );

    // Check if adding this message would exceed the limit
    if (validTimestamps.length >= currentLimit.maxMessages) {
      const oldestRelevantTimestamp = validTimestamps[0];
      const timeUntilAllowed = Math.ceil(
        (currentLimit.window - (now - oldestRelevantTimestamp)) / 1000,
      );

      return {
        allowed: false,
        reason: t("chat:rateLimiting.tooManyMessages"),
        timeRemaining: timeUntilAllowed,
      };
    }

    return { allowed: true, isWorkspaceBlocked: false };
  }, [rateLimitState, workspaceData, activeConversation, t]);

  // Update rate limit state after sending a message
  const updateRateLimitAfterSend = useCallback(() => {
    const now = Date.now();
    const currentLimit = RATE_LIMITS[rateLimitState.level];

    setRateLimitState((prev) => {
      // Add current timestamp
      const newTimestamps = [...prev.messageTimestamps, now];

      // Clean old timestamps
      const validTimestamps = newTimestamps.filter(
        (timestamp) => now - timestamp < currentLimit.window,
      );

      // Check if we need to escalate restriction level
      let newLevel = prev.level;
      let newViolation = prev.lastViolation;
      let newCooldownEnd = prev.cooldownEnd;

      // If we're at the limit, escalate
      if (validTimestamps.length >= currentLimit.maxMessages) {
        newViolation = now;

        // Escalate restriction level
        if (prev.level === "normal") newLevel = "warning";
        else if (prev.level === "warning") newLevel = "restricted";
        else if (prev.level === "restricted") newLevel = "severe";
        // severe stays severe

        // Set cooldown period
        const newLimitConfig = RATE_LIMITS[newLevel];

        if (newLimitConfig.cooldown > 0) {
          newCooldownEnd = now + newLimitConfig.cooldown;
        }
      }

      return {
        level: newLevel,
        messageTimestamps: validTimestamps,
        lastViolation: newViolation,
        cooldownEnd: newCooldownEnd,
        remainingCooldown: newCooldownEnd ? newCooldownEnd - now : 0,
      };
    });
  }, [rateLimitState.level]);

  // Update the debounced function for marking messages as read
  const debouncedMarkAsRead = useDebouncedCallback(async (conversationId) => {
    if (!conversationId || !hasFocus) return;

    try {
      // Update local messages immediately to mark them as read
      setLocalMessages((prevLocalMessages) => {
        if (!prevLocalMessages) return prevLocalMessages;

        const newData = { ...prevLocalMessages };

        // Iterate through messages from newest to oldest
        newData.data.forEach((dayEntry) => {
          dayEntry.groups.forEach((group) => {
            if (group.subgroups) {
              // Handle special groups with subgroups
              group.subgroups.forEach((subgroup) => {
                // Only update messages from other users
                if (subgroup.user.id !== currentUser?.id) {
                  subgroup.messages.forEach((message) => {
                    // Stop if we encounter a message that was already read
                    if (message.is_read) {
                      return;
                    }

                    // Mark message as read
                    if (!message.read_by) {
                      message.read_by = {};
                    }
                    message.read_by[currentUser?.id!] = true;
                    message.is_read = true;
                  });
                }
              });
            } else {
              // Handle regular groups
              if (group.messages && group.messages.user.id !== currentUser?.id) {
                group.messages.messages.forEach((message) => {
                  // Stop if we encounter a message that was already read
                  if (message.is_read) {
                    return;
                  }

                  // Mark message as read
                  if (!message.read_by) {
                    message.read_by = {};
                  }
                  message.read_by[currentUser?.id!] = true;
                  message.is_read = true;
                });
              }
            }
          });
        });

        return newData;
      });

      // Send the API request
      await markConversationAsRead(conversationId);
    } catch (error) {
      console.error("Failed to mark conversation as read:", error);
    }
  }, 3000); // Changed from 30000 to 15000 (15 seconds)

  // Add this effect to track document focus/blur
  useEffect(() => {
    const handleFocus = () => setHasFocus(true);
    const handleBlur = () => setHasFocus(false);

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  const isCurrentUser = (userId: string | undefined) => {
    return userId === currentUser?.id;
  };

  // Message editing state
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingPendingMessageId, setEditingPendingMessageId] = useState<string | null>(null);
  const [editMessageContent, setEditMessageContent] = useState("");
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Refs
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isInitialLoadRef = useRef(true);
  const prevScrollHeightRef = useRef(0);
  const scrollPositionRef = useRef(0);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  // Add a new ref to track if we're near the bottom of the chat
  const isNearBottomRef = useRef(true);
  const isAdjustingScrollRef = useRef(false); // Corrected initialization
  const hasScrolledToBottomRef = useRef(false); // Add this to track if we've scrolled to bottom initially

  // TanStack Query for infinite scrolling
  const {
    data: messagesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    refetch,
  } = useInfiniteQuery({
    queryKey: [
      "messages",
      activeConversation?.id || conversationId,
      type || "normal",
      ...(type === "lesson" ? [workspaceId, lessonId] : []),
    ],
    queryFn: async ({ pageParam = 0 }) => {
      if (!activeConversation && !conversationId) return { data: [] };
      if (type === "lesson" && lessonId && workspaceId) {
        const { data } = await getLessonConversationMessages(workspaceId, lessonId, pageParam, 20);

        trackNewMessages(data);

        return data;
      }
      const { data } = await getConversationMessages(
        activeConversation?.id || conversationId,
        pageParam,
        20,
      );

      trackNewMessages(data);

      return data;
    },
    getNextPageParam: (lastPage, allPages) => {
      // If the last page has no data, there are no more pages to load
      let groupCount = 0;

      lastPage.data.forEach((arr) => (groupCount += arr.groups.length));
      if (!lastPage.data || groupCount < 20) return undefined;

      // Calculate the next offset based on the current number of pages
      return allPages.length * 20;
    },
    initialPageParam: 0, // Add this line to fix the TypeScript error
    enabled: !!(activeConversation || conversationId),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Merge messages data from different pages
  const messages = useMemo(() => {
    if (!messagesData) return null;

    // Create a map to store merged day entries
    const dateMap = new Map<string, DayEntry>();

    // Process each page
    messagesData.pages.forEach((page) => {
      if (!page.data) return;

      // For each day entry in the page
      page.data.forEach((dayEntry) => {
        const existingEntry = dateMap.get(dayEntry.date);

        if (existingEntry) {
          // If this date already exists, merge the groups
          const mergedGroups = [...existingEntry.groups];

          // Add new groups from this page
          dayEntry.groups.forEach((newGroup) => {
            // Check if this is a special group with subgroups
            if (newGroup.special_tag && newGroup.subgroups) {
              // Find an existing special group with the same special_tag
              const existingGroupIndex = mergedGroups.findIndex(
                (g) => g.special_tag === newGroup.special_tag,
              );

              if (existingGroupIndex !== -1) {
                // If the special group exists, merge the subgroups
                const existingGroup = mergedGroups[existingGroupIndex];

                // Ensure the existing group has subgroups property
                if (existingGroup.subgroups) {
                  // For each new subgroup, check if we need to merge or add
                  newGroup.subgroups.forEach((newSubgroup) => {
                    const existingSubgroupIndex = existingGroup.subgroups!.findIndex(
                      (sg) => sg.user.id === newSubgroup.user.id,
                    );

                    if (existingSubgroupIndex !== -1) {
                      // Merge messages from the same user in the subgroup (prepend older messages)
                      existingGroup.subgroups![existingSubgroupIndex].messages = [
                        ...newSubgroup.messages,
                        ...existingGroup.subgroups![existingSubgroupIndex].messages,
                      ];
                    } else {
                      // Add new subgroup
                      existingGroup.subgroups!.push(newSubgroup);
                    }
                  });
                } else {
                  // If somehow the existing group doesn't have subgroups, replace it
                  mergedGroups[existingGroupIndex] = newGroup;
                }
              } else {
                // If this is a new special group, add it
                mergedGroups.push(newGroup);
              }
            } else {
              // Handle regular groups
              const existingGroupIndex = mergedGroups.findIndex(
                (g) =>
                  g.group_timestamp === newGroup.group_timestamp &&
                  !g.special_tag &&
                  g.messages?.user?.id === newGroup.messages?.user?.id,
              );

              if (existingGroupIndex !== -1) {
                // If the group exists, merge the messages (prepend older messages)
                mergedGroups[existingGroupIndex].messages.messages = [
                  ...newGroup.messages.messages,
                  ...mergedGroups[existingGroupIndex].messages.messages,
                ];
              } else {
                // Otherwise add the new group
                mergedGroups.push(newGroup);
              }
            }
          });

          // Update the entry with merged groups
          dateMap.set(dayEntry.date, {
            ...existingEntry,
            groups: mergedGroups,
          });
        } else {
          // If this is a new date, add it to the map
          dateMap.set(dayEntry.date, dayEntry);
        }
      });
    });

    // Convert the map back to an array and sort by date (newest first)
    const mergedData = Array.from(dateMap.values()).sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });

    return {
      data: mergedData,
    };
  }, [messagesData]);

  // Initialize and update localMessages when messages change (including new pages from infinite query)
  useEffect(() => {
    if (messages) {
      setLocalMessages(messages);
    }
  }, [messages]);

  // Helper function to add message to local state
  const addMessageToLocalState = useCallback(
    (message: Message & { user?: any }) => {
      // Use localMessages if available, otherwise fall back to messages
      const currentMessages = localMessages || messages;

      if (!currentMessages) return;

      setLocalMessages((prevLocalMessages) => {
        // If prevLocalMessages is null, use the current messages as base
        const baseMessages = prevLocalMessages || currentMessages;
        const newData = { ...baseMessages };

        // Format the message date to user's timezone
        const messageDate = format(
          toZonedTime(new Date(message.created_at), currentUser?.timezone ?? "UTC"),
          "yyyy-MM-dd",
        );

        // Find the entry for the message's date or create it
        let dateEntry = newData.data.find((entry) => entry.date === messageDate);

        if (!dateEntry) {
          dateEntry = {
            date: messageDate,
            groups: [],
          };
          newData.data.unshift(dateEntry); // Add to beginning since newest first
        }

        // Handle messages with special_tag
        if (message.special_tag) {
          // Find existing group with matching group_key across all dates
          let foundGroup: MessageGroup | null = null;
          let foundDateEntry: typeof dateEntry | null = null;

          for (const dayEntry of newData.data) {
            const group = dayEntry.groups.find((g) => g.group_key === message.special_tag);

            if (group) {
              foundGroup = group;
              foundDateEntry = dayEntry;
              break;
            }
          }

          if (foundGroup && foundDateEntry) {
            // Remove the group from its current position
            foundDateEntry.groups = foundDateEntry.groups.filter(
              (g) => g.group_key !== message.special_tag,
            );

            // Update the group timestamp to the new message timestamp
            foundGroup.group_timestamp = message.created_at;

            // Find or create subgroup for this user
            if (!foundGroup.subgroups) {
              foundGroup.subgroups = [];
            }

            const userSubgroup = foundGroup.subgroups.find(
              (sub) => sub.user.id === message.user?.id,
            );

            if (
              userSubgroup &&
              shouldGroupMessages(
                message,
                userSubgroup.messages[0],
                message.user?.id,
                userSubgroup.user.id,
              )
            ) {
              // Add to existing subgroup (at the beginning since messages are newest first)
              userSubgroup.messages.unshift(message);
              // Update subgroup timestamp
              userSubgroup.group_timestamp = message.created_at;
            } else {
              // Create new subgroup for this user (at the beginning since newest first)
              foundGroup.subgroups.unshift({
                user: message.user!,
                messages: [message],
                group_timestamp: message.created_at,
              });
            }

            // Move the updated group to the top of the newest date (today)
            dateEntry.groups.unshift(foundGroup);
          } else {
            // Group not found - refetch to get fresh data
            console.log("Special group not found, refetching messages...");
            refetch();

            return prevLocalMessages;
          }
        } else {
          // Handle messages without special_tag (regular messages)
          const newestGroup = dateEntry.groups[0];

          // Check if we should add to existing regular group or create new one
          if (newestGroup && newestGroup.group_key === "regular" && newestGroup.messages) {
            // Check if we can group with the existing regular group
            if (
              shouldGroupMessages(
                message,
                newestGroup.messages.messages[0],
                message.user?.id,
                newestGroup.messages.user.id,
              )
            ) {
              // Add to existing regular group
              newestGroup.messages.messages.unshift(message);
              newestGroup.group_timestamp = message.created_at;
            } else {
              // Create new regular group since users don't match or time gap is too large
              const newGroup: MessageGroup = {
                group_key: "regular",
                group_timestamp: message.created_at,
                special_tag: null,
                messages: {
                  user: message.user!,
                  messages: [message],
                },
              };

              dateEntry.groups.unshift(newGroup);
            }
          } else {
            // No regular group at the top or no groups at all - create new regular group
            const newGroup: MessageGroup = {
              group_key: "regular",
              group_timestamp: message.created_at,
              special_tag: null,
              messages: {
                user: message.user!,
                messages: [message],
              },
            };

            dateEntry.groups.unshift(newGroup);
          }
        }

        return newData;
      });
    },
    [messages, localMessages, currentUser?.timezone, refetch],
  );

  // Helper function to update read status locally
  const updateReadStatusLocally = useCallback(
    (userId: string) => {
      // Use localMessages if available, otherwise fall back to messages
      const currentMessages = localMessages || messages;

      if (!currentMessages) return;

      setLocalMessages((prevLocalMessages) => {
        // If prevLocalMessages is null, use the current messages as base
        const baseMessages = prevLocalMessages || currentMessages;
        const newData = { ...baseMessages };
        let foundFirstRead = false;

        // Loop through all messages from newest to oldest (normal order)
        newData.data.forEach((dayEntry) => {
          dayEntry.groups.forEach((group) => {
            if (group.subgroups) {
              // Handle special groups with subgroups
              group.subgroups.forEach((subgroup) => {
                // Skip messages from the user who triggered the event (userId)
                // Only process messages from OTHER users
                if (subgroup.user.id !== userId) {
                  // Search from the beginning (newest messages first)
                  for (let i = 0; i < subgroup.messages.length; i++) {
                    const message = subgroup.messages[i];

                    // Stop if we encounter a message that was already read by this user
                    if (message.read_by && message.read_by[userId]) {
                      foundFirstRead = true;
                      break;
                    }

                    // Update every unread message
                    if (!message.read_by) {
                      message.read_by = {};
                    }
                    if (!message.read_by[userId]) {
                      message.read_by[userId] = true;
                      message.is_read = true;
                    }
                  }
                }
              });
            } else {
              // Handle regular groups
              if (group.messages && group.messages.user.id !== userId) {
                // Search from the beginning (newest messages first)
                for (let i = 0; i < group.messages.messages.length; i++) {
                  const message = group.messages.messages[i];

                  // Stop if we encounter a message that was already read by this user
                  if (message.read_by && message.read_by[userId]) {
                    foundFirstRead = true;
                    break;
                  }

                  // Update every unread message
                  if (!message.read_by) {
                    message.read_by = {};
                  }
                  if (!message.read_by[userId]) {
                    message.read_by[userId] = true;
                    message.is_read = true;
                  }
                }
              }
            }

            // If we found the first read message, stop searching
            if (foundFirstRead) return;
          });

          // If we found the first read message, stop searching
          if (foundFirstRead) return;
        });

        return newData;
      });
    },
    [messages, localMessages],
  );

  // Helper function to update message locally (including replacing localId with realId)
  const updateMessageLocally = useCallback(
    (messageId: string, newContent: string, historyOfChanges?: any) => {
      setLocalMessages((prevLocalMessages) => {
        if (!messages) return prevLocalMessages;
        const newData = prevLocalMessages ? { ...prevLocalMessages } : messages;

        newData.data.forEach((dayEntry) => {
          dayEntry.groups.forEach((group) => {
            if (group.subgroups) {
              group.subgroups.forEach((subgroup) => {
                subgroup.messages.forEach((message) => {
                  if (message.id === messageId) {
                    message.message = newContent;
                    if (historyOfChanges) {
                      message.history_of_changes = historyOfChanges;
                    }
                  }
                });
              });
            } else {
              if (group.messages) {
                group.messages.messages.forEach((message) => {
                  if (message.id === messageId) {
                    message.message = newContent;
                    if (historyOfChanges) {
                      message.history_of_changes = historyOfChanges;
                    }
                  }
                });
              }
            }
          });
        });

        return newData;
      });
    },
    [messages, localMessages],
  );

  // Helper function to delete message locally
  const deleteMessageLocally = useCallback(
    (messageId: string) => {
      setLocalMessages((prevLocalMessages) => {
        if (!messages) return prevLocalMessages;
        const newData = prevLocalMessages ? { ...prevLocalMessages } : messages;

        newData.data.forEach((dayEntry) => {
          dayEntry.groups = dayEntry.groups
            .map((group) => {
              if (group.subgroups) {
                return {
                  ...group,
                  subgroups: group.subgroups
                    .map((subgroup) => ({
                      ...subgroup,
                      messages: subgroup.messages.filter((message) => message.id !== messageId),
                    }))
                    .filter((subgroup) => subgroup.messages.length > 0),
                };
              } else {
                return {
                  ...group,
                  messages: group.messages
                    ? {
                        ...group.messages,
                        messages: group.messages.messages.filter(
                          (message) => message.id !== messageId,
                        ),
                      }
                    : group.messages,
                };
              }
            })
            .filter(
              (group) =>
                (group.subgroups && group.subgroups.length > 0) ||
                (group.messages && group.messages.messages.length > 0),
            );
        });

        // Remove empty day entries
        newData.data = newData.data.filter((dayEntry) => dayEntry.groups.length > 0);

        return newData;
      });
    },
    [messages, localMessages],
  );

  // Track new messages for animation - moved before useInfiniteQuery
  const trackNewMessages = useCallback(
    (newMessages: MessageData) => {
      if (!messages && !localMessages) return;

      const currentMessages = localMessages || messages;

      if (!currentMessages) return;

      const existingMessageIds = new Set();

      // Collect existing message IDs
      currentMessages.data.forEach((dayEntry) => {
        dayEntry.groups.forEach((group) => {
          if (group.special_tag && group.subgroups) {
            // Handle special groups with subgroups
            group.subgroups.forEach((subgroup) => {
              subgroup.messages.forEach((msg) => {
                existingMessageIds.add(msg.id);
              });
            });
          } else {
            // Handle regular groups
            group.messages.messages.forEach((msg) => {
              existingMessageIds.add(msg.id);
            });
          }
        });
      });

      // Collect new message IDs
      const newMessageIds = new Set<string>();

      newMessages.data.forEach((dayEntry) => {
        dayEntry.groups.forEach((group) => {
          if (group.special_tag && group.subgroups) {
            // Handle special groups with subgroups
            group.subgroups.forEach((subgroup) => {
              subgroup.messages.forEach((msg) => {
                if (!existingMessageIds.has(msg.id)) {
                  newMessageIds.add(msg.id);
                }
              });
            });
          } else {
            // Handle regular groups
            group.messages.messages.forEach((msg) => {
              if (!existingMessageIds.has(msg.id)) {
                newMessageIds.add(msg.id);
              }
            });
          }
        });
      });

      // Update animated message IDs
      if (newMessageIds.size > 0) {
        setAnimatedMessageIds(newMessageIds);
      }
    },
    [messages, localMessages], // Updated dependencies
  );

  // Add the missing functions after the trackNewMessages function
  // Improve the checkIfNearBottom function
  const checkIfNearBottom = useCallback(() => {
    if (!scrollAreaRef.current) return true;

    const scrollContainer = scrollAreaRef.current.querySelector(
      "[data-radix-scroll-area-viewport]",
    );

    if (!scrollContainer) return true;

    // Calculate how far we are from the bottom
    const distanceFromBottom =
      scrollContainer.scrollHeight - (scrollContainer.scrollTop + scrollContainer.clientHeight);

    // Consider "near bottom" if within ~3 messages height (roughly 300px)
    const isNearBottom = distanceFromBottom < 300;

    // Update the ref
    isNearBottomRef.current = isNearBottom;

    return isNearBottom;
  }, []);

  // Add a function to scroll to bottom
  const scrollToBottom = useCallback((instant?: boolean) => {
    if (!scrollAreaRef.current) return;

    const scrollContainer = scrollAreaRef.current.querySelector(
      "[data-radix-scroll-area-viewport]",
    );

    if (!scrollContainer) return;

    // For instant scrolling, use scrollTop directly
    if (instant) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;

      return;
    }

    // Use requestAnimationFrame to ensure the DOM has updated before scrolling
    requestAnimationFrame(() => {
      // Use smooth scrolling behavior with better timing
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: instant ? "instant" : "smooth",
      });

      // Reduce the double-check delay for better responsiveness
      setTimeout(() => {
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: instant ? "instant" : "smooth",
        });
      }, 50);
    });
  }, []);

  // Maintain scroll position when new messages are loaded
  useEffect(() => {
    // Only run this effect when we finish loading more messages (not on initial load)
    if (isInitialLoadRef.current || !hasScrolledToBottomRef.current) return;

    if (!scrollAreaRef.current) return;

    const scrollContainer = scrollAreaRef.current.querySelector(
      "[data-radix-scroll-area-viewport]",
    );

    if (!scrollContainer) return;

    // When loading starts, we don't do anything
    if (isFetchingNextPage) return;

    // When loading finishes, we need to adjust the scroll position
    if (!isFetchingNextPage && isAdjustingScrollRef.current) {
      // Calculate how much the content height has changed
      const heightDifference = scrollContainer.scrollHeight - prevScrollHeightRef.current;

      // If height increased (new messages loaded at top), adjust scroll position
      if (heightDifference > 0) {
        // Set scroll position to maintain the same view
        // This keeps the user looking at the same messages they were viewing before
        scrollContainer.scrollTop = scrollPositionRef.current + heightDifference;
      }

      // Clean up
      scrollContainer.classList.remove("loading-messages");
      isAdjustingScrollRef.current = false;
      lastMessageRef.current = null;
    }
  }, [isFetchingNextPage]);

  // Add the useEffect for marking messages as read
  useEffect(() => {
    if (!activeConversation || !hasFocus || !messages || !currentUser) return;

    // Find the last message that wasn't from the current user
    let lastNonCurrentUserMessage = null;

    if (messages.data && messages.data.length > 0) {
      // Iterate through all days (most recent first)
      for (const dayEntry of messages.data) {
        if (!dayEntry.groups || dayEntry.groups.length === 0) continue;

        // Iterate through all groups in this day (most recent first)
        for (const group of dayEntry.groups) {
          // Check if this is a special group with subgroups
          if (group.special_tag && group.subgroups) {
            // Find the most recent message across all subgroups that's not from current user
            let latestNonCurrentUserTimestamp = 0;
            let candidateMessage = null;

            group.subgroups.forEach((subgroup) => {
              if (
                subgroup.messages &&
                subgroup.messages.length > 0 &&
                !isCurrentUser(subgroup.user.id)
              ) {
                const subgroupLatestMessage = subgroup.messages[0];
                const msgTimestamp = new Date(subgroupLatestMessage.created_at).getTime();

                if (msgTimestamp > latestNonCurrentUserTimestamp) {
                  candidateMessage = subgroupLatestMessage;
                  latestNonCurrentUserTimestamp = msgTimestamp;
                }
              }
            });

            if (candidateMessage) {
              lastNonCurrentUserMessage = candidateMessage;
              break;
            }
          } else if (
            group.messages &&
            group.messages.messages &&
            group.messages.messages.length > 0 &&
            !isCurrentUser(group.messages.user.id)
          ) {
            lastNonCurrentUserMessage = group.messages.messages[0];
            break;
          }
        }

        // If we found a message, break out of the day loop
        if (lastNonCurrentUserMessage) break;
      }
    }

    const isRead =
      lastNonCurrentUserMessage && lastNonCurrentUserMessage.read_by
        ? Object.keys(lastNonCurrentUserMessage.read_by).some((key) => key === currentUser.id)
        : false;

    if (lastNonCurrentUserMessage && !isRead) {
      debouncedMarkAsRead(activeConversation.id);
    }
  }, [hasFocus, activeConversation, messages]);

  // Focus input when conversation changes
  useEffect(() => {
    if (inputRef.current && !lessonId && !isSending) {
      inputRef.current.focus();
    }
  }, [activeConversation, messages, isSending]);

  // Update the Echo whisper listeners
  const { leaveChannel: leaveCreated, channel } = useEcho(
    `private-conversation.${activeConversation?.id ?? conversationId}`,
    ".message_created",
    (data: any) => {
      // Check if the message is from the current user
      const isFromCurrentUser = data.user && currentUser && data.user.id === currentUser.id;

      if (isFromCurrentUser) {
        // This is our own message, try to match it with a pending message in localMessages
        const messageContent = Array.isArray(data.message) ? data.message[0] : data.message;
        let foundPendingMessage = false;

        // First, try to update any pending message
        setLocalMessages((prevLocalMessages) => {
          if (!prevLocalMessages) return prevLocalMessages;

          const newData = { ...prevLocalMessages };

          newData.data.forEach((dayEntry) => {
            dayEntry.groups.forEach((group) => {
              if (group.subgroups) {
                group.subgroups.forEach((subgroup) => {
                  subgroup.messages.forEach((message) => {
                    const extendedMessage = message as ExtendedMessage;

                    if (extendedMessage.pending && extendedMessage.message === messageContent) {
                      // Replace the pending message with real data but preserve files
                      const preservedFiles = extendedMessage.files;
                      const preservedLocalId = extendedMessage.localId;

                      Object.assign(extendedMessage, {
                        id: data.id,
                        message: messageContent,
                        created_at: data.created_at,
                        created_time: format(
                          toZonedTime(new Date(data.created_at), currentUser?.timezone ?? "UTC"),
                          "HH:mm",
                        ),
                        special_tag: data.special_tag,
                        has_attachments: data.has_attachments || false,
                        attachments: data.attachments || [],
                        history_of_changes: data.history_of_changes,
                        is_read: data.is_read || false,
                        read_by: data.read_by || null,
                        pending: false,
                        // Preserve the localId to prevent re-renders
                        localId: preservedLocalId,
                        // Preserve the files array to prevent reloads and shifts
                        files: preservedFiles,
                      });

                      foundPendingMessage = true;
                    }
                  });
                });
              } else {
                if (group.messages) {
                  group.messages.messages.forEach((message) => {
                    const extendedMessage = message as ExtendedMessage;

                    if (extendedMessage.pending && extendedMessage.message === messageContent) {
                      // Replace the pending message with real data but preserve files
                      const preservedFiles = extendedMessage.files;
                      const preservedLocalId = extendedMessage.localId;

                      Object.assign(extendedMessage, {
                        id: data.id,
                        message: messageContent,
                        created_at: data.created_at,
                        created_time: format(
                          toZonedTime(new Date(data.created_at), currentUser?.timezone ?? "UTC"),
                          "HH:mm",
                        ),
                        special_tag: data.special_tag,
                        has_attachments: data.has_attachments || false,
                        attachments: data.attachments || [],
                        history_of_changes: data.history_of_changes,
                        is_read: data.is_read || false,
                        read_by: data.read_by || null,
                        pending: false,
                        // Preserve the localId to prevent re-renders
                        localId: preservedLocalId,
                        // Preserve the files array to prevent reloads and shifts
                        files: preservedFiles,
                      });

                      foundPendingMessage = true;
                    }
                  });
                }
              }
            });
          });

          return newData;
        });

        // Check if the message ended up in the wrong group type after updating
        if (foundPendingMessage) {
          setLocalMessages((prevLocalMessages) => {
            if (!prevLocalMessages) return prevLocalMessages;

            const newData = { ...prevLocalMessages };

            newData.data.forEach((dayEntry) => {
              dayEntry.groups.forEach((group, groupIndex) => {
                // Check if special_tag message is in regular group
                if (data.special_tag && group.group_key === "regular" && group.messages) {
                  const messageIndex = group.messages.messages.findIndex(
                    (msg) => msg.id === data.id,
                  );

                  if (messageIndex !== -1) {
                    const messageToMove = group.messages.messages[messageIndex];

                    // Remove from regular group
                    group.messages.messages.splice(messageIndex, 1);

                    // If group is now empty, remove it
                    if (group.messages.messages.length === 0) {
                      dayEntry.groups.splice(groupIndex, 1);
                    }

                    // Add to correct group using existing logic
                    const realMessage: Message & { user?: any } = {
                      ...messageToMove,
                      user: data.user,
                    };

                    // Use addMessageToLocalState in the next tick to avoid conflicts
                    setTimeout(() => {
                      addMessageToLocalState(realMessage);
                    }, 0);
                  }
                }
                // Check if regular message is in special group
                else if (!data.special_tag && group.group_key !== "regular" && group.subgroups) {
                  group.subgroups.forEach((subgroup, subgroupIndex) => {
                    const messageIndex = subgroup.messages.findIndex((msg) => msg.id === data.id);

                    if (messageIndex !== -1) {
                      const messageToMove = subgroup.messages[messageIndex];

                      // Remove from subgroup
                      subgroup.messages.splice(messageIndex, 1);

                      // If subgroup is now empty, remove it
                      if (subgroup.messages.length === 0) {
                        group.subgroups!.splice(subgroupIndex, 1);
                      }

                      // If group has no more subgroups, remove the group
                      if (group.subgroups!.length === 0) {
                        dayEntry.groups.splice(groupIndex, 1);
                      }

                      // Add to correct group using existing logic
                      const realMessage: Message & { user?: any } = {
                        ...messageToMove,
                        user: data.user,
                      };

                      // Use addMessageToLocalState in the next tick to avoid conflicts
                      setTimeout(() => {
                        addMessageToLocalState(realMessage);
                      }, 0);
                    }
                  });
                }
              });
            });

            return newData;
          });
        }

        // If we didn't find a pending message, treat it as a new message
        if (!foundPendingMessage) {
          const newMessage: Message & { user?: any } = {
            id: data.id,
            message: messageContent,
            created_at: data.created_at,
            created_time: format(
              toZonedTime(new Date(data.created_at), currentUser?.timezone ?? "UTC"),
              "HH:mm",
            ),
            special_tag: data.special_tag,
            has_attachments: data.has_attachments || false,
            attachments: data.attachments || [],
            history_of_changes: data.history_of_changes,
            is_read: data.is_read || false,
            read_by: data.read_by || null,
            user: data.user,
          };

          addMessageToLocalState(newMessage);
        }

        // Auto-scroll if near bottom
        if (isNearBottomRef.current) {
          setTimeout(() => scrollToBottom(true), 50);
        }
      } else {
        // This is a message from someone else, add it to local state
        const newMessage: Message & { user?: any } = {
          id: data.id,
          message: Array.isArray(data.message) ? data.message[0] : data.message,
          created_at: data.created_at,
          created_time: format(
            toZonedTime(new Date(data.created_at), currentUser?.timezone ?? "UTC"),
            "HH:mm",
          ),
          special_tag: data.special_tag,
          has_attachments: data.has_attachments || false,
          attachments: data.attachments || [],
          history_of_changes: data.history_of_changes,
          is_read: data.is_read || false,
          read_by: data.read_by || null,
          user: data.user,
        };

        addMessageToLocalState(newMessage);

        // Auto-scroll if near bottom
        if (isNearBottomRef.current) {
          setTimeout(() => scrollToBottom(true), 100);
        }

        // Call the callback for new messages from others
        if (onNewMessageFromOthers) {
          onNewMessageFromOthers(newMessage);
        }
      }
    },
    [activeConversation, currentUser, onNewMessageFromOthers, addMessageToLocalState],
  );

  // Update the messages_read event handler
  const { leaveChannel: leaveRead } = useEcho(
    `private-conversation.${activeConversation?.id ?? conversationId}`,
    ".messages_read",
    (data: any) => {
      // Update read status locally instead of refetching
      updateReadStatusLocally(data.user_id);
    },
    [activeConversation, updateReadStatusLocally],
  );

  // Update the message_updated event handler
  const { leaveChannel: leaveUpdate } = useEcho(
    `private-conversation.${activeConversation?.id ?? conversationId}`,
    ".message_updated",
    (data: any) => {
      // Check if this is from the current user (sender) or another user (receiver)
      const isFromCurrentUser = data.user && currentUser && data.user.id === currentUser.id;

      if (isFromCurrentUser) {
        // For sender: only update the history_of_changes to show edit history in tooltip
        updateMessageLocally(data.id, data.message[0], data.history_of_changes);
      } else {
        // For receiver: update both content and history_of_changes
        updateMessageLocally(data.id, data.message[0], data.history_of_changes);
      }
    },
    [activeConversation, updateMessageLocally, currentUser],
  );

  // Update the message_deleted event handler
  const { leaveChannel: leaveDelete } = useEcho(
    `private-conversation.${activeConversation?.id ?? conversationId}`,
    ".message_deleted",
    (data: any) => {
      // Delete message locally instead of refetching
      deleteMessageLocally(data.id);
    },
    [activeConversation, deleteMessageLocally],
  );

  // Update the Echo whisper listeners
  useEffect(() => {
    if (!channel()) return;

    channel().listenForWhisper("typing", (data: any) => {
      if (data.user && data.user !== currentUser?.id) {
        // Find the user in the conversation members
        const typingUser = activeConversation?.members.find(
          (member) => member.user.id === data.user,
        )?.user;

        if (typingUser) {
          setTypingUsers((prev) => ({
            ...prev,
            [data.user]: {
              timestamp: Date.now(),
              user: typingUser,
            },
          }));
        }
      }
    });

    channel().listenForWhisper("stoppedTyping", (data: any) => {
      if (data.user && data.user !== currentUser?.id) {
        setTypingUsers((prev) => {
          const updated = { ...prev };

          delete updated[data.user];

          return updated;
        });
      }
    });

    return () => {
      channel().stopListeningForWhisper("typing");
      channel().stopListeningForWhisper("stoppedTyping");
    };
  }, [channel, currentUser?.id, activeConversation]);

  useEffect(() => {
    if (inputRef.current && currentUser?.id) {
      const input = inputRef.current;

      // Add focus handler to send typing if input already has content
      input.onfocus = () => {
        if (messageInput.length > 0) {
          channel().whisper("typing", { user: currentUser?.id });
        }
      };
      // Add blur handler to send stoppedTyping when input loses focus
      input.onblur = () => {
        if (messageInput.length > 0) {
          channel().whisper("stoppedTyping", { user: currentUser?.id });
        }
      };
    }
  }, [messageInput.length, currentUser?.id, channel]);

  useEffect(() => {
    return () => {
      leaveCreated();
      leaveUpdate();
      leaveRead();
      leaveDelete();
    };
  }, [leaveCreated, leaveUpdate, leaveRead, leaveDelete]);

  // Focus edit textarea when editing starts
  useEffect(() => {
    if (editingMessageId || editingPendingMessageId) {
      if (editTextareaRef.current) {
        editTextareaRef.current.focus();
        // Place cursor at the end of the text
        const length = editTextareaRef.current.value.length;

        editTextareaRef.current.setSelectionRange(length, length);
      }
    }
  }, [editingMessageId, editingPendingMessageId]);

  // Scroll to bottom only on initial load
  useEffect(() => {
    if (
      scrollAreaRef.current &&
      view === "conversation" &&
      isInitialLoadRef.current &&
      messages &&
      !isFetchingNextPage &&
      !hasScrolledToBottomRef.current
    ) {
      const scrollContainer = scrollAreaRef.current.querySelector(
        "[data-radix-scroll-area-viewport]",
      );

      if (scrollContainer) {
        // Create a MutationObserver to watch for DOM changes
        const observer = new MutationObserver(() => {
          // Instantly scroll to bottom when content changes
          scrollContainer.scrollTop = scrollContainer.scrollHeight;

          // Mark that we've scrolled to bottom
          hasScrolledToBottomRef.current = true;
          isInitialLoadRef.current = false;

          // Disconnect the observer after scrolling
          observer.disconnect();
        });

        // Start observing the scroll container for child list changes
        observer.observe(scrollContainer, { childList: true, subtree: true });

        // Also try to scroll immediately in case content is already there
        scrollContainer.scrollTop = scrollContainer.scrollHeight;

        // Clean up observer after a timeout to prevent memory leaks
        setTimeout(() => {
          observer.disconnect();
        }, 1000);
      }
    }
  }, [messages, view, isFetchingNextPage]);

  const openConversation = async (conversationId: string) => {
    try {
      setView("conversation");
      isInitialLoadRef.current = true;
      hasScrolledToBottomRef.current = false; // Reset scroll flag for new conversation
      isAdjustingScrollRef.current = false; // Reset adjusting flag

      // Reset local messages to ensure fresh data
      setLocalMessages(null);

      // First check if the conversation data is already available in the store
      const { openConversations } = useChatStore.getState();
      const existingConversation = openConversations.find((conv) => conv.id === conversationId);

      if (existingConversation) {
        // Use the existing conversation data from the store
        setActiveConversation(existingConversation);

        // Force a refetch immediately to ensure we have the latest messages
        // This is especially important for popup chats that might not have been listening for events
        await refetch();

        return existingConversation;
      }

      // If not in store, fetch the conversation data
      const {
        data: { data: conversationData },
      } = await getConversationData(conversationId);

      setActiveConversation(conversationData);

      // Force a refetch immediately to ensure we have the latest messages
      // This is especially important for popup chats that might not have been listening for events
      await refetch();

      return conversationData;
    } catch (error: any) {
      console.error(error);

      if (error.response?.data?.code === "doesnt_belong_to_conversation") {
        // Handle specific error for user not belonging to conversation
        toast(t("chat:errors.doesntBelongToConversation"), {
          description: t("chat:errors.doesntBelongToConversation"),
          type: "error",
        });
      } else {
        toast.error(t("common:error"), {
          description: t("chat:errors.loadConversation"),
        });
      }
    }
  };

  // Validate files before upload
  const validateFiles = (files: File[]): string | null => {
    // Check file count
    if (files.length > MAX_FILE_COUNT) {
      return t("chat:fileErrors.countExceeded", { maxCount: MAX_FILE_COUNT });
    }

    // Check individual file size and type
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        return t("chat:fileErrors.sizeExceeded", {
          fileName: file.name,
          maxSize: MAX_FILE_SIZE / (1024 * 1024),
        });
      }

      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        return t("chat:fileErrors.typeNotAllowed", { fileType: file.type });
      }
    }

    // Check total size for this message
    const totalSize = files.reduce((total, file) => total + file.size, 0);

    if (totalSize > MAX_TOTAL_SIZE) {
      return t("chat:fileErrors.totalSizeExceeded", {
        totalSize: (totalSize / (1024 * 1024)).toFixed(2),
        maxSize: MAX_TOTAL_SIZE / (1024 * 1024),
      });
    }

    // Check conversation storage limit if conversation data is available
    if (activeConversation?.storage_usage) {
      const conversationUsedSize = parseInt(activeConversation.storage_usage.total_size);
      const newTotalSize = conversationUsedSize + totalSize;

      if (newTotalSize > MAX_CONVERSATION_SIZE) {
        return t("chat:fileErrors.conversationSizeExceeded", {
          totalSize: (totalSize / (1024 * 1024)).toFixed(2),
          maxSize: MAX_CONVERSATION_SIZE / (1024 * 1024 * 1024),
        });
      }

      // Check if conversation can upload
      if (!activeConversation.storage_usage.can_upload) {
        return t("chat:fileErrors.conversationFull");
      }
    }

    return null;
  };

  // Update the sendMessage function to use proper type casting
  const sendMessage = async () => {
    if (activeConversation && (messageInput.trim().length > 0 || selectedFiles.length > 0)) {
      if (messageInput.length > 5000) {
        toast(t("common:error"), {
          description: t("chat:fileErrors.messageTooLong", { maxLength: 5000 }),
        });

        return;
      }

      // Only proceed if there's actual content (not just whitespace) or files
      if (messageInput.trim().length === 0 && selectedFiles.length === 0) {
        return; // Don't send empty messages
      }

      // Check if any files are invalid
      const invalidFiles = selectedFiles.filter((f) => !f.isValid);

      if (invalidFiles.length > 0) {
        toast(t("common:error"), {
          description: t("chat:fileErrors.invalidFiles", { count: invalidFiles.length }),
        });

        return;
      }

      // Validate the selected files
      const validationError = validateFiles(selectedFiles.map((f) => f.file));

      if (validationError) {
        toast(t("common:error"), {
          description: validationError,
        });

        return;
      }

      // Check rate limit before sending
      const { allowed, reason, timeRemaining } = canSendMessage();

      if (!allowed) {
        toast(t("common:error"), {
          description: reason + (timeRemaining ? ` (${timeRemaining}s)` : ""),
        });

        return;
      }

      const content = messageInput;
      const files = selectedFiles.map((f) => f.file);

      // Clear input immediately
      setMessageInput("");
      setSelectedFiles([]);

      const localId = `local-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      // Create local message with pending status
      const localMessage: ExtendedMessage = {
        id: localId,
        message: content,
        created_at: new Date().toISOString(),
        created_time: format(toZonedTime(new Date(), currentUser?.timezone ?? "UTC"), "HH:mm"),
        special_tag: null,
        has_attachments: files.length > 0,
        attachments: [],
        history_of_changes: null,
        is_read: false,
        read_by: null,
        user: currentUser!,
        pending: true,
        localId: localId,
        files: files,
      };

      // Add local message directly to messages data structure
      setLocalMessages((prevLocalMessages) => {
        const baseData = prevLocalMessages !== null ? prevLocalMessages : messages;

        if (!baseData) return prevLocalMessages;

        const newData = { ...baseData };

        if (!newData.data) return prevLocalMessages;
        const today = format(toZonedTime(new Date(), currentUser?.timezone ?? "UTC"), "yyyy-MM-dd");

        // Find today's entry or create it
        let todayEntry = newData.data.find((entry) => entry.date === today);

        if (!todayEntry) {
          todayEntry = {
            date: today,
            groups: [],
          };
          newData.data.unshift(todayEntry); // Add to beginning since newest first
        }

        // Check if the newest message is from the same user
        const newestGroup = todayEntry.groups[0];
        const shouldCreateNewGroup =
          !newestGroup ||
          (newestGroup.messages &&
            !shouldGroupMessages(
              localMessage,
              newestGroup.messages.messages[0],
              localMessage.user?.id,
              newestGroup.messages.user.id,
            )) ||
          (newestGroup.subgroups &&
            (!newestGroup.subgroups[0] ||
              !shouldGroupMessages(
                localMessage,
                newestGroup.subgroups[0].messages[0],
                localMessage.user?.id,
                newestGroup.subgroups[0].user.id,
              )));

        if (shouldCreateNewGroup) {
          // Create new group
          const newGroup: MessageGroup = {
            group_key: `group_${Date.now()}`,
            group_timestamp: localMessage.created_at,
            special_tag: localMessage.special_tag,
            messages: {
              user: localMessage.user!,
              messages: [localMessage],
            },
          };

          todayEntry.groups.unshift(newGroup);
        } else {
          // Add to existing group
          if (newestGroup.subgroups) {
            // Handle special groups with subgroups
            const userSubgroup = newestGroup.subgroups.find(
              (sub) => sub.user.id === localMessage.user?.id,
            );

            if (
              userSubgroup &&
              shouldGroupMessages(
                localMessage,
                userSubgroup.messages[0],
                localMessage.user?.id,
                userSubgroup.user.id,
              )
            ) {
              userSubgroup.messages.unshift(localMessage);
            } else {
              // Create new subgroup for this user
              newestGroup.subgroups.unshift({
                user: localMessage.user!,
                messages: [localMessage],
                group_timestamp: localMessage.created_at,
              });
            }
          } else {
            // Handle regular groups
            newestGroup.messages!.messages.unshift(localMessage);
          }
        }

        return newData;
      });

      // Scroll to bottom after adding the local message
      setTimeout(scrollToBottom, 50);

      try {
        setIsSending(true);

        // Send the message to the API
        if (type === "lesson" && workspaceId && lessonId) {
          await sendMessageToLessonChat(
            workspaceId,
            lessonId,
            content,
            files.length > 0 ? files : undefined,
          );
        } else if (!type || type === "normal") {
          await sendMessageToApi(
            activeConversation.id,
            content,
            files.length > 0 ? files : undefined,
          );
        }

        // Message sent successfully - the websocket event will handle updating to delivered
        console.log("Message sent, waiting for websocket event");

        // Keep input focused after sending
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 10);
      } catch (error: any) {
        console.error("Failed to send message:", error);

        // Mark message as error in localMessages
        setLocalMessages((prevLocalMessages) => {
          if (!prevLocalMessages) return prevLocalMessages;

          const newData = { ...prevLocalMessages };

          newData.data.forEach((dayEntry) => {
            dayEntry.groups.forEach((group) => {
              if (group.subgroups) {
                group.subgroups.forEach((subgroup) => {
                  subgroup.messages.forEach((message) => {
                    const extendedMessage = message as ExtendedMessage;

                    if (extendedMessage.localId === localId) {
                      extendedMessage.pending = false;
                      extendedMessage.error = true;
                    }
                  });
                });
              } else {
                if (group.messages) {
                  group.messages.messages.forEach((message) => {
                    const extendedMessage = message as ExtendedMessage;

                    if (extendedMessage.localId === localId) {
                      extendedMessage.pending = false;
                      extendedMessage.error = true;
                    }
                  });
                }
              }
            });
          });

          return newData;
        });

        // Handle API validation errors
        if (error.response?.data?.errors) {
          const errorMessages = Object.values(error.response.data.errors).flat();

          toast(t("chat:errors.sendMessage"), {
            description: errorMessages[0] as string,
          });
        } else if (error.response?.data?.code === "doesnt_belong_to_conversation") {
          // Handle specific error for user not belonging to conversation
          toast(t("chat:errors.doesntBelongToConversation"), {
            description: t("chat:errors.doesntBelongToConversation"),
            type: "error",
          });
        } else {
          toast(t("common:error"), {
            description: t("chat:errors.sendMessage"),
          });
        }
      } finally {
        setIsSending(false);
        updateRateLimitAfterSend(); // Update rate limit after sending
      }
    }
  };

  // Start editing a message
  const startEditingMessage = (messageId: string, content: string) => {
    setEditingMessageId(messageId);
    setEditMessageContent(content);
    setEditingPendingMessageId(null);
  };

  // Start editing a pending message
  const startEditingPendingMessage = (messageId: string, content: string) => {
    setEditingPendingMessageId(messageId);
    setEditMessageContent(content);
    setEditingMessageId(null);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingMessageId(null);
    setEditingPendingMessageId(null);
    setEditMessageContent("");
  };

  // Update the saveEditedMessage function to remove pendingMessages references
  const saveEditedMessage = async () => {
    if (!activeConversation) return;

    if (editingMessageId) {
      try {
        // Update the message locally immediately for better UX
        updateMessageLocally(editingMessageId, editMessageContent);

        // Cancel editing immediately for better UX
        cancelEditing();

        // Update the message in the API
        if (type === "lesson" && workspaceId && lessonId) {
          await updateMessageInLessonChat(
            workspaceId,
            lessonId,
            editingMessageId,
            editMessageContent,
          );
        } else if (!type || type === "normal") {
          await updateMessageInApi(activeConversation.id, editingMessageId, editMessageContent);
        }

        // Show success toast
        toast(t("chat:messageEdited"), {
          description: t("chat:messageEditedDescription"),
        });
      } catch (error: any) {
        console.error("Failed to update message:", error);

        if (error.response?.data?.code === "doesnt_belong_to_conversation") {
          // Handle specific error for user not belonging to conversation
          toast(t("chat:errors.doesntBelongToConversation"), {
            description: t("chat:errors.doesntBelongToConversation"),
            type: "error",
          });
        } else {
          toast(t("common:error"), {
            description: t("chat:errors.updateMessage"),
          });
        }
      }
    } else if (editingPendingMessageId) {
      // This case is no longer needed since we don't have separate pendingMessages
      // Just cancel editing
      cancelEditing();
    }
  };

  // Delete a message
  const deleteMessage = async (messageId: string) => {
    if (!activeConversation) return;

    try {
      if (type === "lesson" && workspaceId && lessonId) {
        await deleteMessageFromLessonChat(workspaceId, lessonId, messageId);
      } else if (!type || type === "normal") {
        await deleteMessageFromApi(activeConversation.id, messageId);
      }

      // Remove the message from local state after successful API call
      deleteMessageLocally(messageId);

      // Also remove from pending messages if this message has a matching realMessageId
      // setPendingMessages((prev) => prev.filter((msg) => msg.realMessageId !== messageId)); // This line is removed

      // Show a toast confirming deletion
      toast(t("chat:messageDeleted"), {
        description: t("chat:messageDeletedDescription"),
      });
    } catch (error: any) {
      console.error("Failed to delete message:", error);

      if (error.response?.data?.code === "doesnt_belong_to_conversation") {
        // Handle specific error for user not belonging to conversation
        toast(t("chat:errors.doesntBelongToConversation"), {
          description: t("chat:errors.doesntBelongToConversation"),
          type: "error",
        });
      } else {
        toast(t("common:error"), {
          description: t("chat:errors.deleteMessage"),
        });
      }
    }
  };

  // Remove a pending message
  const removePendingMessage = (messageId: string) => {
    // Remove the pending message from localMessages and clean up blob URLs
    setLocalMessages((prevLocalMessages) => {
      if (!prevLocalMessages) return prevLocalMessages;

      const newData = { ...prevLocalMessages };

      newData.data.forEach((dayEntry) => {
        dayEntry.groups.forEach((group) => {
          if (group.subgroups) {
            group.subgroups.forEach((subgroup) => {
              subgroup.messages = subgroup.messages.filter((message) => {
                const extendedMessage = message as ExtendedMessage;

                if (extendedMessage.localId === messageId || extendedMessage.id === messageId) {
                  // No need to clean up fileUrls since we're creating them on-demand now
                  return false; // Remove this message
                }

                return true; // Keep this message
              });
            });
          } else {
            if (group.messages) {
              group.messages.messages = group.messages.messages.filter((message) => {
                const extendedMessage = message as ExtendedMessage;

                if (extendedMessage.localId === messageId || extendedMessage.id === messageId) {
                  // No need to clean up fileUrls since we're creating them on-demand now
                  return false; // Remove this message
                }

                return true; // Keep this message
              });
            }
          }
        });
      });

      return newData;
    });

    setSelectedFiles([]);
    setMessageInput("");
    cancelEditing();
  };

  // Retry a failed message

  // Remove or modify the backToList function since we don't need it anymore
  const backToList = () => {
    // This function is no longer needed but we'll keep it for compatibility
    // with other components that might use it
    setSelectedFiles([]);
    setMessageInput("");
    cancelEditing();
  };

  // Handle key press in message input
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle key press in edit textarea
  const handleEditKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      saveEditedMessage();
    } else if (e.key === "Escape") {
      cancelEditing();
    }
  };

  // Handle typing indicator
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    setMessageInput(newValue);

    // Only send typing event if there's at least 1 character
    if (newValue.length >= 1 && messageInput.length === 0) {
      // User started typing - send typing event
      channel().whisper("typing", { user: currentUser?.id });
    } else if (newValue.length === 0 && messageInput.length > 0) {
      // User cleared input - send stopped typing event
      channel().whisper("stoppedTyping", { user: currentUser?.id });
    }
  };

  // Add debounced function to stop typing after inactivity
  const debouncedStopTyping = useDebouncedCallback(() => {
    if (messageInput.length > 0) {
      channel().whisper("stoppedTyping", { user: currentUser?.id });
    }
  }, 2000); // Stop typing after 2 seconds of inactivity

  // Call debounced stop typing whenever input changes
  useEffect(() => {
    if (messageInput.length > 0) {
      debouncedStopTyping();
    }
  }, [messageInput, debouncedStopTyping]);

  const handleFiles = (files: File[]) => {
    if (selectedFiles.length + files.length > MAX_FILE_COUNT) {
      toast(t("common:error"), {
        description: t("chat:fileErrors.countExceeded", { maxCount: MAX_FILE_COUNT }),
      });

      return;
    }

    // Validate each file individually
    const validatedFiles = files.map((file) => {
      let isValid = true;
      let validationError = "";

      // Check individual file size
      if (file.size > MAX_FILE_SIZE) {
        isValid = false;
        validationError = t("chat:fileErrors.sizeExceeded", {
          fileName: file.name,
          maxSize: MAX_FILE_SIZE / (1024 * 1024),
        });
      }

      // Check file type
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        isValid = false;
        validationError = t("chat:fileErrors.typeNotAllowed", { fileType: file.type });
      }

      return {
        file,
        isValid,
        validationError,
      };
    });

    // Check total size for this message
    const totalSize = validatedFiles.reduce((total, { file }) => total + file.size, 0);

    if (totalSize > MAX_TOTAL_SIZE) {
      toast(t("common:error"), {
        description: t("chat:fileErrors.totalSizeExceeded", {
          totalSize: (totalSize / (1024 * 1024)).toFixed(2),
          maxSize: MAX_TOTAL_SIZE / (1024 * 1024),
        }),
      });

      return;
    }

    // Check conversation storage limit if conversation data is available
    if (activeConversation?.storage_usage) {
      const conversationUsedSize = parseInt(activeConversation.storage_usage.total_size);
      const newTotalSize = conversationUsedSize + totalSize;

      if (newTotalSize > MAX_CONVERSATION_SIZE) {
        toast(t("common:error"), {
          description: t("chat:fileErrors.conversationSizeExceeded", {
            totalSize: (totalSize / (1024 * 1024)).toFixed(2),
            maxSize: MAX_CONVERSATION_SIZE / (1024 * 1024 * 1024),
          }),
        });

        return;
      }

      // Check if conversation can upload
      if (!activeConversation.storage_usage.can_upload) {
        toast(t("common:error"), {
          description: t("chat:fileErrors.conversationFull"),
        });

        return;
      }
    }

    setSelectedFiles((prev) => [...prev, ...validatedFiles]);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
      e.dataTransfer.clearData();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (e.clipboardData.files && e.clipboardData.files.length > 0) {
      e.preventDefault();
      handleFiles(Array.from(e.clipboardData.files));
    }
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);

      // Check if adding these files would exceed the maximum
      if (selectedFiles.length + filesArray.length > MAX_FILE_COUNT) {
        toast(t("common:error"), {
          description: t("chat:fileErrors.countExceeded", { maxCount: MAX_FILE_COUNT }),
        });

        return;
      }

      // Validate each file individually
      const validatedFiles = filesArray.map((file) => {
        let isValid = true;
        let validationError = "";

        // Check individual file size
        if (file.size > MAX_FILE_SIZE) {
          isValid = false;
          validationError = t("chat:fileErrors.sizeExceeded", {
            fileName: file.name,
            maxSize: MAX_FILE_SIZE / (1024 * 1024),
          });
        }

        // Check file type
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
          isValid = false;
          validationError = t("chat:fileErrors.typeNotAllowed", { fileType: file.type });
        }

        return {
          file,
          isValid,
          validationError,
        };
      });

      setSelectedFiles((prev) => [...prev, ...validatedFiles]);

      // Reset the file input to allow selecting the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Remove a selected file
  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Trigger file input click
  const handleAttachmentClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";

    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const handleBlockUser = (userId: string) => {
    setBlockedUsers((prev) => [...prev, userId]);
  };

  // Handle emoji insertion
  const insertEmoji = (emoji: string) => {
    if (!inputRef.current) return;

    const input = inputRef.current;
    const startPos = input.selectionStart || 0;
    const endPos = input.selectionEnd || 0;

    // Create new text with emoji inserted at cursor position
    const newText = messageInput.substring(0, startPos) + emoji + messageInput.substring(endPos);

    // Update the input value
    setMessageInput(newText);

    // After React updates the DOM, set the cursor position after the inserted emoji
    setTimeout(() => {
      const newCursorPos = startPos + emoji.length;

      input.focus();
      input.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Add a separate effect for pending messages to ensure smooth scrolling

  // Get workspace blocking status for UI components
  const getWorkspaceBlockingStatus = useCallback(() => {
    if (!workspaceData || !activeConversation) {
      return {
        isBlocked: false,
        hasLeft: false,
        shouldShowIndicator: false,
      };
    }

    const isBlocked = workspaceData.is_workspace_blocked_for_user;
    const hasLeft = workspaceData.has_user_left_workspace;
    const conversationType = activeConversation.type;

    // For individual conversations: only show if blocked
    if (conversationType === "individual") {
      return {
        isBlocked,
        hasLeft: false, // Left status doesn't matter for individual conversations
        shouldShowIndicator: isBlocked,
      };
    }

    // For group conversations: show if blocked OR left
    if (conversationType === "group") {
      return {
        isBlocked,
        hasLeft,
        shouldShowIndicator: isBlocked || hasLeft,
      };
    }

    return {
      isBlocked: false,
      hasLeft: false,
      shouldShowIndicator: false,
    };
  }, [workspaceData, activeConversation]);

  // Add scrollToBottom to the returned functions
  // Add typingUsers to the return object at the end of the hook
  return {
    isOpen,
    setIsOpen,
    messageInput,
    setMessageInput,
    activeConversation,
    view,
    blockedUsers,
    currentUser,
    conversationList,
    messages: localMessages || messages, // Use localMessages if available, otherwise fall back to messages
    isLoading: status === "pending",
    selectedFiles,
    selectedFilesCount, // For backward compatibility
    isSending,
    editingMessageId,
    editingPendingMessageId,
    editMessageContent,
    animatedMessageIds,
    isFetchingNextPage,
    hasNextPage,
    typingUsers,
    rateLimitState,

    // Refs
    editTextareaRef,
    scrollAreaRef,
    inputRef,
    isInitialLoadRef,
    isAdjustingScrollRef,
    hasScrolledToBottomRef,
    fileInputRef,
    activeChannel: channel,
    // Functions
    fetchNextPage,
    openConversation,
    validateFiles,
    sendMessage,
    startEditingMessage,
    startEditingPendingMessage,
    cancelEditing,
    setEditMessageContent,
    saveEditedMessage,
    deleteMessage,
    removePendingMessage,

    backToList,
    handleKeyPress,
    handleEditKeyPress,
    handleInputChange,
    handleFileChange,
    removeFile,
    handleAttachmentClick,
    isCurrentUser,
    formatFileSize,
    handleBlockUser,
    scrollToBottom,
    checkIfNearBottom,
    trackNewMessages,
    hasFocus,
    insertEmoji,
    canSendMessage,
    getWorkspaceBlockingStatus,
    isDragging,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handlePaste,
  };
}
